import {
  Checkbox,
  Grid,
  InfoIcon,
  TextHighlight,
  TextSize,
  makeStyles
} from '@focaldata/cin-ui-components'
import { isMultilangPocEnabled } from '../../../../constants/featureFlags'
import useNeedsTranslationVar from '../../../../hooks/localState/useNeedsTranslationVar'
import { useIsAudienceReadOnly } from '../../../../hooks/useIsAudienceReadOnly'

// cin-ui already renders MUI's "small" checkbox (20px glyph); take it down
// another notch so the box doesn't compete with the questionnaire action icons
// beside it. The label stays at TextSize.ms to match "Show audience questions".
const useStyles = makeStyles()({
  compact: {
    '& .MuiSvgIcon-root': {
      fontSize: 16
    }
  },
  // cin-ui's Checkbox forces the tick's colour via `& span.MuiButtonBase-root`
  // regardless of `disabled`, which otherwise leaves a disabled control looking
  // identical to an enabled one. Opacity on the whole row is what actually
  // reads as "locked" — it can't be fought by a nested colour override.
  locked: {
    opacity: 0.5
  }
})

const HELP_TEXT =
  "Tick this when respondents need the questionnaire in a language it isn't written in. It adds the Translations step, where you translate the questionnaire into each language allocated on the Audience tab, and launch is blocked until they're complete. Leave it clear when the questionnaire is already written in the language respondents will see."

const LOCKED_TEXT =
  "This survey already has stored translations, so this can't be cleared here — remove them in the Translations tab if they're not needed."

export const NeedsTranslationToggle = () => {
  const { classes } = useStyles()
  const { needsTranslation, setNeedsTranslation, hasStoredTranslations } =
    useNeedsTranslationVar()
  const { isReadOnly } = useIsAudienceReadOnly()

  if (!isMultilangPocEnabled()) {
    return null
  }

  // Stored translations force `needsTranslation` on regardless of any click
  // (see useNeedsTranslationVar) — locking the control here avoids a click
  // visibly reverting itself on the next render.
  const disabled = isReadOnly || hasStoredTranslations

  return (
    <Grid item xs="auto">
      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        className={disabled ? classes.locked : undefined}
      >
        <Checkbox
          className={classes.compact}
          ariaLabel="Needs translation"
          label="Needs translation"
          textSize={TextSize.ms}
          textHighlight={TextHighlight.Background}
          checked={needsTranslation}
          disabled={disabled}
          onChange={() => setNeedsTranslation(!needsTranslation)}
        />
        <InfoIcon text={hasStoredTranslations ? LOCKED_TEXT : HELP_TEXT} />
      </Grid>
    </Grid>
  )
}
